import React, { createContext, useContext, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export interface TabsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

export function Tabs({
  value: controlledValue,
  defaultValue,
  onValueChange,
  className,
  children,
}: TabsProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue || '');
  const value = controlledValue ?? uncontrolledValue;

  const handleValueChange = (newValue: string) => {
    if (!controlledValue) {
      setUncontrolledValue(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleValueChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  className?: string;
  children: React.ReactNode;
  /** Accessible name for the tablist. Required when multiple tablists appear on the same page. */
  'aria-label'?: string;
}

export function TabsList({ className, children, 'aria-label': ariaLabel }: TabsListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  /**
   * W2-I: keyboard navigation for tablist (WCAG 2.1 Success Criterion 2.1.1).
   *
   * WAI-ARIA Authoring Practices require:
   *   - Arrow Left/Right: move focus to the previous/next tab
   *   - Home: move focus to the first tab
   *   - End: move focus to the last tab
   *
   * This uses event delegation on the list container so individual TabsTrigger
   * components don't need to know their siblings.
   */
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!listRef.current) return;

    const tabs = Array.from(
      listRef.current.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])'),
    );
    if (tabs.length === 0) return;

    const currentIndex = tabs.findIndex((t) => t === document.activeElement);

    let nextIndex: number | null = null;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'Home':
        e.preventDefault();
        nextIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    if (nextIndex !== null) {
      tabs[nextIndex].focus();
    }
  }

  return (
    <div
      ref={listRef}
      className={cn(
        'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        className,
      )}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  value: string;
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

export function TabsTrigger({ value, className, children, disabled, ...rest }: TabsTriggerProps) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs');
  }

  const isSelected = context.value === value;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isSelected}
      /**
       * W2-I: aria-controls is intentionally omitted.
       * Some callers use Tabs without TabsContent (content is rendered separately
       * via conditional logic). In those cases aria-controls would reference a
       * non-existent element and trigger aria-valid-attr-value (WCAG 4.1.2).
       *
       * aria-controls is optional for role="tab" — WAI-ARIA does not list it in
       * aria-required-attr for tabs. axe does not flag its absence. Screen readers
       * can locate the active panel via aria-selected and DOM order.
       *
       * Callers who need explicit aria-controls can spread it via {...rest}.
       */
      data-state={isSelected ? 'active' : 'inactive'}
      disabled={disabled}
      /**
       * W2-I: roving tabindex pattern (WAI-ARIA tablist).
       * Only the active tab is reachable via Tab key (tabIndex=0).
       * Inactive tabs are skipped (tabIndex=-1) and only reachable via Arrow keys.
       * This avoids the user having to Tab through every tab trigger to reach
       * the tab panel content.
       */
      tabIndex={isSelected ? 0 : -1}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        isSelected
          ? 'bg-background text-foreground shadow-sm'
          : 'hover:bg-background/50 hover:text-foreground',
        className,
      )}
      onClick={() => context.onValueChange(value)}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function TabsContent({ value, className, children }: TabsContentProps) {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('TabsContent must be used within Tabs');
  }

  const isSelected = context.value === value;

  if (!isSelected) return null;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      /**
       * W2-I: tabpanel is focusable so users can Tab into the panel content
       * after navigating to the active tab with Arrow keys.
       */
      tabIndex={0}
      className={cn(
        'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
    >
      {children}
    </div>
  );
}
